import React, { memo, PropsWithChildren, useState } from "react";

import { logAnalyticsEvent } from "@/constants/amplitude";
import { FemaleBodyFatRangeTypes } from "@/constants/bodyMetrics";
import { mapBodyFatToBodyType } from "@/constants/bodyType";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { ExperimentIds } from "@/constants/experiments";
import { Languages } from "@/constants/languages";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { GymType, TrainingEnvironment } from "@/constants/trainingEnvironment";
import { useLocalization } from "@/hooks/useLocalization";
import { AuthTokenDetails } from "@/types/api";
import { Genders } from "@/types/gender";
import getEquipment, {
    EquipmentInterface,
    wholeHomeEquipment,
} from "@/utils/services/getEquipment";
import {
    friendsContactId,
    friendsUserId,
    utmCampaign as utm_campaign,
    utmContent as utm_content,
} from "@/utils/settings";

import {
    getAuthTokenApi,
    getProfileApi,
    getUserUuidApi,
    purchaseProductApi,
    updateProfileApi,
} from "./services/api";
import { Answers } from "./AnswersProvider";
import { useExperiment } from "./ExperimentsProvider";
import { useGender } from "./GenderProvider";
import { localStorageManager, LocalStorageManagerKeys } from "./localStorageManager";
import { AmplitudeTracker, PaltaDataPlatformTracker } from "./trackers";
import useAnswers from "./useAnswers";

interface UpdateUserData {
    receiveEmail?: boolean;
    profile?: unknown;
    postcode?: string;
}

interface PurchaseFeatureData {
    ident: string;
    featureName: string | null;
    eventScreen: string;
    price?: string;
    onComplete?: () => void;
    onError?: () => void;
}

interface ServerProviderProps {
    uuid: string | null;
    isLoading: boolean;
    createUser: (
        userEmail: string
    ) => Promise<{ uuid: string | undefined; isAuth0AccountExists: boolean }>;
    setUUID: React.Dispatch<React.SetStateAction<string | null>>;
    getProfile: (uuid: string) => Promise<unknown>;
    updateUser: (data: UpdateUserData) => Promise<void>;
    purchaseFeature: (data: PurchaseFeatureData) => Promise<void>;
}

export const defaultProps: ServerProviderProps = {
    uuid: null,
    isLoading: false,
    createUser: () => Promise.resolve({ uuid: undefined, isAuth0AccountExists: false }),
    setUUID: () => null,
    updateUser: () => Promise.resolve(undefined),
    getProfile: () => Promise.resolve({}),
    purchaseFeature: () => Promise.resolve(undefined),
};

export const ServerContext = React.createContext<ServerProviderProps>(defaultProps);

const fillCreateUserRequestBody = (
    userEmail: string,
    answers: Answers,
    name: string,
    gender: Genders,
    funnelLanguage: Languages,
    isMaleCalisthenicsTest?: boolean
) => {
    const mutatedAnswers = { ...answers };
    if (!mutatedAnswers[QUESTIONS_IDS.BODY_TYPE] && gender === Genders.Female) {
        if (answers[QUESTIONS_IDS.BODY_FAT]) {
            mutatedAnswers[QUESTIONS_IDS.BODY_TYPE] =
                mapBodyFatToBodyType[answers[QUESTIONS_IDS.BODY_FAT] as FemaleBodyFatRangeTypes];
        }
    }

    let mappedEnvironment = isMaleCalisthenicsTest
        ? TrainingEnvironment.Home
        : TrainingEnvironment.Gym;
    if (answers?.location) {
        if (answers?.location === "outdoor") {
            mappedEnvironment = TrainingEnvironment.Gym;
        } else {
            mappedEnvironment = Array.isArray(answers?.location)
                ? answers?.location?.length > 1
                    ? TrainingEnvironment.Mix
                    : (answers?.location[0] as TrainingEnvironment)
                : (answers?.location as TrainingEnvironment);
        }
    }

    const mappedGymEquipment = getEquipment(mappedEnvironment, answers?.gym_type as GymType);
    const homeEquipment =
        (answers[QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT] as unknown as Array<EquipmentInterface>) ??
        (mappedEnvironment === TrainingEnvironment.Gym ? wholeHomeEquipment : []);
    if (mappedEnvironment === TrainingEnvironment.Home) {
        delete mutatedAnswers.gym_type;
    }

    const language = [Languages.English, Languages.Spanish].includes(funnelLanguage)
        ? funnelLanguage
        : Languages.English;

    return {
        email: userEmail,
        profile: {
            ...mutatedAnswers,
            preferred_environment: mappedEnvironment,
            gender,
            equipment_by_environment: [...homeEquipment, ...mappedGymEquipment],
            invited_by_uuid: friendsUserId,
            contact_id_for_inviter: friendsContactId,
            utm_campaign,
            utm_content,
            language,
            name,
            ...(isMaleCalisthenicsTest
                ? {
                      calisthenics_workout_plan: true,
                  }
                : {}),
        },
        receive_email: false,
        registration_source: "web",
    };
};

export const fillUpdateUserRequestBody = ({ receiveEmail, profile, postcode }: UpdateUserData) => {
    let request = {};

    if (profile) {
        request = {
            ...request,
            profile: { ...profile },
        };
    }

    if (receiveEmail !== undefined) {
        request = {
            ...request,
            receive_email: receiveEmail,
        };
    }

    if (postcode !== undefined) {
        request = {
            ...request,
            postcode,
        };
    }

    return request;
};

const ServerProvider = ({ children }: PropsWithChildren) => {
    const { gender } = useGender();
    const { language } = useLocalization();

    const { isTest: isMaleCalisthenicsTest } = useExperiment({
        experimentId: ExperimentIds.MALE_CALISTHENICS,
    });

    const savedUUID =
        (localStorageManager.getStoredItem(LocalStorageManagerKeys.USER_UUID) as string) ?? null;
    const [uuid, setUUID] = useState<string | null>(savedUUID);
    const [isLoading, setIsLoading] = useState(false);
    const { answers, setAnswers, name } = useAnswers();

    const handleSetUuid = (uuid: string) => {
        localStorageManager.storeItem(LocalStorageManagerKeys.USER_UUID, uuid);
        AmplitudeTracker.setUserId(uuid);
        PaltaDataPlatformTracker.setUserId(uuid);

        setUUID(uuid);
    };

    const createUser = async (userEmail: string) => {
        setIsLoading(true);
        const request = fillCreateUserRequestBody(
            userEmail,
            answers,
            name,
            gender,
            language,
            isMaleCalisthenicsTest
        );
        const data = await getUserUuidApi(request);
        let isAuth0AccountExists = false;
        if (data?.uuid) {
            const { uuid } = data;
            handleSetUuid(uuid);

            const result = await getAuthTokenApi(data?.uuid);

            if (result?.detail === AuthTokenDetails.Auth0AccExists) {
                isAuth0AccountExists = true;
            }
        }

        setIsLoading(false);

        return { uuid: data?.uuid, isAuth0AccountExists };
    };

    const updateUser = async (data: UpdateUserData) => {
        const request = fillUpdateUserRequestBody(data);

        if (uuid) {
            const data = await getAuthTokenApi(uuid);

            if (data?.auth_token) {
                await updateProfileApi(data.auth_token, request, uuid);
            }
        }
    };

    const getProfile = async (uuid: string) => {
        const data = await getAuthTokenApi(uuid);
        if (data?.auth_token) {
            const profile = await getProfileApi(data.auth_token);
            if (profile) {
                setAnswers(profile as Answers);
                return profile;
            }
        }
    };

    const purchaseFeature = async ({
        ident,
        featureName,
        eventScreen,
        price,
        onComplete,
        onError,
    }: PurchaseFeatureData) => {
        logAnalyticsEvent(WebEventTypes.PURCHASE_STARTED, {
            [WebEventProps.SCREEN]: eventScreen,
            [WebEventProps.TYPE]: featureName,
            [WebEventProps.PRICE]: price || null,
        });
        await purchaseProductApi(
            {
                uuid,
                ident,
                featureName,
            },
            () => {
                logAnalyticsEvent(WebEventTypes.PURCHASE_COMPLETED, {
                    [WebEventProps.SCREEN]: eventScreen,
                    [WebEventProps.TYPE]: featureName,
                    [WebEventProps.PRICE]: price || null,
                    [WebEventProps.PRODUCT_ID]: ident,
                });
                onComplete && onComplete();
            },
            () => {
                logAnalyticsEvent(WebEventTypes.PURCHASE_FAILED, {
                    [WebEventProps.SCREEN]: eventScreen,
                    [WebEventProps.TYPE]: featureName,
                    [WebEventProps.PRICE]: price || null,
                });
                onError && onError();
            }
        );
    };

    return (
        <ServerContext.Provider
            value={{
                createUser,
                uuid,
                setUUID,
                updateUser,
                getProfile,
                purchaseFeature,
                isLoading,
            }}
        >
            {children}
        </ServerContext.Provider>
    );
};

export default memo(ServerProvider);
